/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { Fragment, useEffect } from "react";
import {
  createBrowserRouter,
  type NavigateOptions,
  Outlet,
  type Params,
  type RouteObject,
  useMatch,
  useParams,
} from "react-router-dom";

import { DefaultLayout } from "./default-layout";
import { EventEmitter } from "./event-emitter";
import { DynamicLayout } from "./layout";
import { Route, RouteParameters } from "./route";
import { View } from "./view";

export interface RouterEventPayload<T extends string | never = never> {
  readonly path: string;
  readonly parameters?: Params<T>;
}

/**
 * This components is used to fire an event when a new route is opened.
 *
 * It'd be nice to use `react-router`'s `loader` function for this. However, we run into an edge case:
 * The `loader` function is called the first time a route is mounted and is not re-called until the
 * route is unmounted entirely and then re-mounted.
 *
 * This is fine for most cases, but if we have _child_ routes, the _parent_ route will not be un-mounted.
 * So if you are in a child route, then navigate back to the parent route, `loader` isn't called.
 *
 * This components is a workaround for this issue: It fires an event when a route is opened, no matter
 * if it's the first time or not. It utilizes the `useMatch` hook from `react-router-dom` to detect
 * path changes, and then fires `onPathLoaded` when the path is matched.
 */
const ElementThatFiresWhenItIsOpenedInARoute = ({
  children,
  pathsForRouteMatching,
  onPathLoaded,
}: {
  children: React.ReactNode;
  pathsForRouteMatching: string[];
  onPathLoaded: (params: Params<string>) => void;
}) => {
  const params = useParams();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const matches = pathsForRouteMatching.map((path) => useMatch(path));

  useEffect(() => {
    if (!matches.some(Boolean)) {
      return;
    }

    onPathLoaded(params);
  }, [matches]);
  return children;
};

export type RouterEventType =
  | "navigated-to-path"
  | "route-loaded"
  | "new-router-created";

export class Router<AppState> extends EventEmitter<
  RouterEventType,
  RouterEventPayload
> {
  public _router = createBrowserRouter([{}]);
  private readonly _routes: Route<any, AppState, any>[] = [];
  private _overlay?: View<AppState, DynamicLayout>;

  private _lastRouteEvent?: RouterEventPayload;

  /** method relying on the react-router-dom api to generate all the routes */
  private _buildRoute(
    route: Route<any, AppState, any>,
    parentRoute?: Route<any, AppState, any>
  ): RouteObject {
    /**
     * contains the paths to check for route matches. if this is a child route, an entry `${parentRoutePath}/${routePath}`
     * is automatically created to support relative paths in child routes.
     */
    const pathsForRouteMatching = [route.path];

    if (parentRoute) {
      pathsForRouteMatching.push(`${parentRoute.path}/${route.path}`);
    }

    return {
      path: route.path,
      children: route.children.map((child) => this._buildRoute(child, route)),
      // for details on why we use `ElementThatFiresWhenItIsOpenedInARoute`, see the comment above
      element: (
        <ElementThatFiresWhenItIsOpenedInARoute
          pathsForRouteMatching={pathsForRouteMatching}
          onPathLoaded={(params) => {
            // only call events in the next tick as otherwise the app bootstrapping
            // process might not yet be done and events might be broadcast even though
            // listeners aren't yet setup

            this.fireRouteEvent({
              path: route.path,
              parameters: params,
              route,
            });
          }}
        >
          {route.children.length ? (
            <>
              {route.view.render()}
              {route.isRenderingAllChildren
                ? route.children.map((child, index) => (
                    <Fragment key={index}>{child.view.render()}</Fragment>
                  ))
                : null}
              <Outlet />
            </>
          ) : (
            <>{route.view.render()}</>
          )}
        </ElementThatFiresWhenItIsOpenedInARoute>
      ),
    } satisfies RouteObject;
  }

  fireRouteEvent(routeEvent: {
    path: string;
    parameters: Params<string>;
    route: Route;
  }) {
    if (JSON.stringify(this._lastRouteEvent) === JSON.stringify(routeEvent)) {
      return;
    }

    routeEvent.route.fireRouteEvent("load", {
      path: routeEvent.path,
      parameters: routeEvent.parameters,
    });
    this._lastRouteEvent = routeEvent;

    this.fireEvent("route-loaded", routeEvent);
  }

  private _buildOverlayRoute(
    overlay: View<AppState, any>,
    routes: Route<any, AppState, any>[]
  ) {
    return {
      element: overlay.render(),
      children: routes
        /** if a route should not be visible for some reason or is outside overlay it should not be rendered */
        .filter((route) => route.isOverlayChild && route.isVisible)
        .map((route) => this._buildRoute(route)),
    } satisfies RouteObject;
  }

  private _buildRouter() {
    try {
      const routesNotChildrenOfOverlay = this._routes
        .filter((x) => !x.isOverlayChild)
        .map((route) => this._buildRoute(route));
      this._router = createBrowserRouter(
        this._overlay
          ? [
              this._buildOverlayRoute(this._overlay, this._routes),
              ...routesNotChildrenOfOverlay,
            ]
          : this._routes.map((route) => this._buildRoute(route))
      );
      this.fireEvent("new-router-created");
    } catch (e) {
      console.log("An error occured during router creation");
      console.log(e);
    }
  }

  createRoute<
    RoutePathParamKeys extends string = "",
    Layout extends DynamicLayout = DynamicLayout,
  >(
    routeParameters: RouteParameters<AppState, Layout>
  ): Route<RoutePathParamKeys, AppState, Layout> {
    const {
      path,
      view,
      isVisible = true,
      isOverlayChild = true,
      isRenderingAllChildren = false,
    } = routeParameters;
    const route = new Route<RoutePathParamKeys, AppState, Layout>(
      path,
      view,
      isVisible,
      isOverlayChild,
      isRenderingAllChildren
    );

    // recreate router when single routes change
    route.on("change", () => this._buildRouter());

    this._routes.push(route);
    this._buildRouter();
    return route;
  }

  createOverlayView<Layout extends DynamicLayout = DynamicLayout>({
    name,
    layout = new DefaultLayout() as Layout,
  }: {
    name: string;
    layout?: Layout;
  }): View<AppState, DynamicLayout> {
    this._overlay = new View<AppState, Layout>(name, layout);
    return this._overlay;
  }

  navigate(path: string, options?: NavigateOptions) {
    this._router.navigate(path, options);
    this.fireEvent("navigated-to-path", { path });
  }
}
